import { useState } from "react";
import { useAuth } from "@/modules/auth";
import Container from "@/components/ui/container";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { copyToClipboard } from "@/lib/utils";

type Platform = {
	id: string;
	name: string;
	logo: string;
	logoType: "image" | "letter";
	color: string;
	exportSteps: string[];
	helpUrl?: string;
};

const platforms: Platform[] = [
	{
		id: "senja",
		name: "Senja",
		logo: "/images/logo-senja.png",
		logoType: "image",
		color: "bg-orange-500",
		exportSteps: ["Log in to your Senja dashboard", "Go to Settings → Export Data", "Select 'Testimonials' and click Export", "Download the CSV or JSON file"],
		helpUrl: "https://support.senja.io/articles/how-do-i-download-export-my-testimonials-xudvt",
	},
	{
		id: "famewall",
		name: "FameWall",
		logo: "/images/logo-famewall.png",
		logoType: "image",
		color: "bg-blue-600",
		exportSteps: ["Log in to your FameWall account", "Navigate to Settings → Data Export", "Select all testimonials you want to export", "Click 'Export' and download the file"],
		helpUrl: "https://famewall.io",
	},
	{
		id: "testimonial",
		name: "Testimonial.to",
		logo: "/images/logo-testimonialto.png",
		logoType: "image",
		color: "bg-purple-600",
		exportSteps: ["Log in to your Testimonial.to dashboard", "Go to your Space settings", "Find the Export option in the menu", "Download your testimonials as CSV"],
		helpUrl: "https://help.testimonial.to/en/articles/6304554-export-video-and-text-testimonials",
	},
	{
		id: "feedspace",
		name: "Feedspace",
		logo: "/images/logo-feedspace.png",
		logoType: "image",
		color: "bg-blue-500",
		exportSteps: ["Log in to your Feedspace dashboard", "Go to Settings → Export", "Select the testimonials you want to export", "Download your data as CSV or JSON"],
		helpUrl: "https://feedspace.io",
	},
	{
		id: "other",
		name: "Other Platform",
		logo: "?",
		logoType: "letter",
		color: "bg-gray-500",
		exportSteps: [
			"Export your testimonials from your current platform",
			"Most platforms support CSV or JSON export",
			"If you can't find an export option, take screenshots of your testimonials",
			"We'll help you migrate regardless of the format",
		],
	},
];

export default function ImportPage() {
	const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
	const [emailCopied, setEmailCopied] = useState(false);
	const { currentWorkspaceId } = useAuth();

	const handleSelectPlatform = (platform: Platform) => {
		setSelectedPlatform(platform);
		if (currentWorkspaceId) {
			useOnboardingStore.getState().completeStep(currentWorkspaceId, "import_reviews");
		}
	};

	return (
		<Container className="py-12">
			{!selectedPlatform ? (
				// Platform selection view
				<div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
					<h2 className="text-xl font-semibold text-primary mb-2">Import from another platform</h2>
					<p className="text-tertiary text-center max-w-md mb-8">Select your current platform and we'll help you migrate your testimonials.</p>

					<div className="flex flex-col w-full max-w-md bg-white rounded-xl border border-gray-200">
						{platforms.map((platform, index) => (
							<button
								key={platform.id}
								type="button"
								onClick={() => handleSelectPlatform(platform)}
								className={`group flex items-center gap-3.5 px-5 py-4 text-left hover:bg-gray-50 transition-colors ${
									index < platforms.length - 1 ? "border-b border-gray-200" : ""
								} ${index === 0 ? "rounded-t-xl" : ""} ${index === platforms.length - 1 ? "rounded-b-xl" : ""}`}
							>
								{platform.logoType === "image" ? (
									<img src={platform.logo} alt={platform.name} className="w-7 h-7 rounded-md flex-shrink-0 object-contain" />
								) : (
									<div className={`w-7 h-7 ${platform.color} rounded-md flex items-center justify-center text-white font-semibold text-xs flex-shrink-0`}>{platform.logo}</div>
								)}
								<span className="font-medium text-primary text-t-15 flex-1">{platform.name}</span>
								<svg className="w-4 h-4 text-muted group-hover:text-gray-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
								</svg>
							</button>
						))}
					</div>
				</div>
			) : (
				// Instructions view
				<div className="max-w-2xl mx-auto px-4">
					<button type="button" onClick={() => setSelectedPlatform(null)} className="flex items-center gap-2 text-tertiary hover:text-primary mb-8 transition-colors">
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
						</svg>
						Back to platforms
					</button>

					<div className="flex items-center gap-4 mb-8">
						{selectedPlatform.logoType === "image" ? (
							<img src={selectedPlatform.logo} alt={selectedPlatform.name} className="w-10 h-10 rounded-lg object-contain" />
						) : (
							<div className={`w-10 h-10 ${selectedPlatform.color} rounded-lg flex items-center justify-center text-white font-bold text-lg`}>{selectedPlatform.logo}</div>
						)}
						<div>
							<h1 className="text-xl font-semibold text-primary">Import from {selectedPlatform.name}</h1>
							<p className="text-tertiary">Follow these steps to export your testimonials</p>
						</div>
					</div>

					<div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
						<h3 className="font-semibold text-primary mb-4">How to export your data</h3>
						<ol className="space-y-3">
							{selectedPlatform.exportSteps.map((step, index) => (
								<li key={index} className="flex gap-3">
									<span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-tertiary">{index + 1}</span>
									<span className="text-secondary pt-0.5">{step}</span>
								</li>
							))}
						</ol>
						{selectedPlatform.helpUrl && (
							<a href={selectedPlatform.helpUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-4 text-sm text-blue-600 hover:text-blue-700">
								Visit {selectedPlatform.name} help center
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
								</svg>
							</a>
						)}
					</div>

					<div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
						<h3 className="font-semibold text-primary mb-2">Send us your export file</h3>
						<p className="text-secondary text-sm mb-4">Once you've exported your testimonials, email the file to us and we'll import them into your account.</p>
						<div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200">
							<div className="flex-1 min-w-0">
								<p className="text-sm text-gray-600">canyou@sayabout.us</p>
							</div>
							<button
								type="button"
								onClick={async () => {
									await copyToClipboard("canyou@sayabout.us");
									setEmailCopied(true);
									setTimeout(() => setEmailCopied(false), 2000);
								}}
								className="px-4 py-1.5 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
							>
								{emailCopied ? "Copied!" : "Copy"}
							</button>
						</div>
					</div>
				</div>
			)}
		</Container>
	);
}
